import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './ProductSearchModal.css';
import { API_ENDPOINTS, getAuthHeaders } from '../../config/api';
import { loadGacdProductsFromCsv } from '../../lib/catalog/gacd-from-csv.js';
import { searchProducts, findCheapestBySubCategory } from '../../lib/catalog/product-search.js';

/**
 * Hook pour debounce une valeur
 */
function useDebouncedValue(value, delay = 220) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * Mapper les noms de couleurs français vers des codes couleur hex
 */
function getColorHex(colorName) {
  if (!colorName) return null;
  const color = String(colorName).toLowerCase().trim();
  
  const colorMap = {
    'blanc': '#FFFFFF',
    'white': '#FFFFFF',
    'noir': '#000000',
    'black': '#000000',
    'bleu': '#3B82F6',
    'blue': '#3B82F6',
    'vert': '#22C55E',
    'green': '#22C55E',
    'rouge': '#EF4444',
    'red': '#EF4444',
    'rose': '#EC4899',
    'pink': '#EC4899',
    'jaune': '#FBBF24',
    'yellow': '#FBBF24',
    'orange': '#F97316',
    'violet': '#A855F7',
    'purple': '#A855F7',
    'gris': '#6B7280',
    'grey': '#6B7280',
    'beige': '#F5F5DC',
    'marron': '#78350F',
    'brown': '#78350F',
    'transparent': 'rgba(255, 255, 255, 0.1)',
  };
  
  return colorMap[color] || null;
}

/**
 * Formater le nom de couleur pour affichage
 */
function formatColorName(colorName) {
  if (!colorName) return '';
  return String(colorName).charAt(0).toUpperCase() + String(colorName).slice(1).toLowerCase();
}

/**
 * Modal de recherche de produits avec design dark mode
 */
function ProductSearchModal({ isOpen, onClose, onAddProduct }) {
  const STORAGE_KEY = 'productSearchState';
  
  // Restaurer l'état depuis localStorage au montage
  const getPersistedState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          query: parsed.query || '',
          products: parsed.products || [],
          open: parsed.open || false,
          selectedBrand: parsed.selectedBrand || 'Toutes les marques',
          availableBrands: parsed.availableBrands || ['Toutes les marques'],
          selectedSubCategories: parsed.selectedSubCategories || []
        };
      }
    } catch (e) {
      console.warn('Erreur lors de la restauration de l\'état de recherche:', e);
    }
    return {
      query: '',
      products: [],
      open: false,
      selectedBrand: 'Toutes les marques',
      availableBrands: ['Toutes les marques'],
      selectedSubCategories: []
    };
  };
  
  // Sauvegarder l'état dans localStorage
  const saveState = useCallback((state) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        query: state.query,
        products: state.products,
        open: state.open,
        selectedBrand: state.selectedBrand,
        availableBrands: state.availableBrands,
        selectedSubCategories: state.selectedSubCategories || []
      }));
    } catch (e) {
      console.warn('Erreur lors de la sauvegarde de l\'état de recherche:', e);
    }
  }, [STORAGE_KEY]);
  
  const persistedState = getPersistedState();
  
  // État pour le catalogue complet chargé depuis CSV
  const [allProducts, setAllProducts] = useState(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [loadError, setLoadError] = useState(null);
  
  const [query, setQuery] = useState(persistedState.query);
  const debouncedQuery = useDebouncedValue(query, 220);
  const [products, setProducts] = useState(persistedState.products);
  const [productsDetails, setProductsDetails] = useState(new Map()); // Cache des détails produits avec variantes
  const [loadingDetails, setLoadingDetails] = useState(new Set()); // IDs en cours de chargement
  // isLoading n'est plus nécessaire car la recherche est synchrone avec useMemo
  const [activeIndex, setActiveIndex] = useState(0);
  const [open, setOpen] = useState(persistedState.open && persistedState.products.length > 0);
  const [selectedBrand, setSelectedBrand] = useState(persistedState.selectedBrand);
  const [availableBrands, setAvailableBrands] = useState(persistedState.availableBrands);
  const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState(new Map()); // productId -> { size: 'S', packaging: 'Boîte de 100' }
  // Nouveau système de sélections multiples avec quantités
  const [selectedOptions, setSelectedOptions] = useState({}); // productId -> [{ size?: string, color?: string, quantity: number }]
  const [selectedSubCategories, setSelectedSubCategories] = useState(persistedState.selectedSubCategories);
  const [isSubCategoriesDropdownOpen, setIsSubCategoriesDropdownOpen] = useState(false);
  const [showSubCategoriesDropdown, setShowSubCategoriesDropdown] = useState(true);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [cabinet, setCabinet] = useState(null);
  const [distributorAccount, setDistributorAccount] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [emailForm, setEmailForm] = useState({
    subject: 'Commande de matériel dentaire',
    accountNumber: '',
    recipientEmail: '',
    senderEmail: '',
    body: ''
  });
  const [loadingCabinet, setLoadingCabinet] = useState(false);
  
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const brandDropdownRef = useRef(null);
  const subCategoriesDropdownRef = useRef(null);
  const subCategoriesContainerRef = useRef(null);
  const hasRestoredStateRef = useRef(false);

  // Charger tous les produits depuis le CSV au montage (une seule fois)
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setIsLoadingProducts(true);
        setLoadError(null);
        const data = await loadGacdProductsFromCsv();
        if (!cancelled) {
          setAllProducts(data);
          console.log(`✅ [ProductSearch] ${data.length} produits chargés depuis CSV`);
        }
      } catch (err) {
        console.error('❌ [ProductSearch] Erreur chargement produits CSV', err);
        if (!cancelled) {
          setLoadError('Impossible de charger le catalogue produits. Veuillez réessayer.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingProducts(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  // Focus input quand modal s'ouvre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Précharger les données du cabinet au montage du modal
  useEffect(() => {
    if (isOpen && !cabinet) {
      loadCabinetAndAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, cabinet]);

  // Fermer dropdown marques en cliquant dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(event.target)) {
        setIsBrandDropdownOpen(false);
      }
    };
    if (isBrandDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isBrandDropdownOpen]);

  // Fermer dropdown sous-catégories en cliquant dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subCategoriesDropdownRef.current && !subCategoriesDropdownRef.current.contains(event.target)) {
        setIsSubCategoriesDropdownOpen(false);
      }
    };
    if (isSubCategoriesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSubCategoriesDropdownOpen]);

  // Détecter le hard refresh et effacer les sauvegardes
  useEffect(() => {
    // Détecter le type de chargement de la page
    const navigation = performance.getEntriesByType('navigation')[0];
    
    if (navigation) {
      // TYPE_RELOAD = 'reload' signifie un rechargement
      // Pour détecter un hard refresh, on vérifie si c'est un reload ET si le cache a été ignoré
      const isReload = navigation.type === 'reload';
      
      // Vérifier si c'est un hard refresh via le flag dans sessionStorage
      // Un hard refresh vide le sessionStorage, donc si on a un reload mais pas de flag, c'est un hard refresh
      const hadNormalNavigation = sessionStorage.getItem('normalNavigation') === 'true';
      
      if (isReload && !hadNormalNavigation) {
        // Hard refresh détecté - effacer les sauvegardes
        console.log('🔄 [Hard Refresh] Hard refresh détecté, effacement des sauvegardes');
        localStorage.removeItem(STORAGE_KEY);
      }
      
      // Marquer qu'on a eu une navigation normale
      if (!hadNormalNavigation) {
        sessionStorage.setItem('normalNavigation', 'true');
      }
    }

    // Détecter également via les touches clavier (Ctrl+F5, Cmd+Shift+R)
    const handleKeyDown = (event) => {
      // Ctrl+F5 ou Cmd+Shift+R = hard refresh
      if ((event.ctrlKey || event.metaKey) && (event.shiftKey || event.key === 'F5') && (event.key === 'R' || event.key === 'F5')) {
        console.log('🔄 [Hard Refresh] Hard refresh détecté via touches clavier');
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem('normalNavigation'); // Reset pour que le prochain chargement détecte le hard refresh
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Marquer la navigation normale avant le unload
    const handleBeforeUnload = () => {
      sessionStorage.setItem('normalNavigation', 'true');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [STORAGE_KEY]);

  // Note: loadProductDetails n'est plus nécessaire car les produits chargés depuis CSV
  // ont déjà toutes les variantes. Les détails sont initialisés dans l'useEffect ci-dessous.

  // Extraire les sous-catégories disponibles depuis les produits recherchés
  const availableSubCategories = useMemo(() => {
    if (!products.length) return [];
    // Adapter selon le format: Product utilise subCategory (camelCase)
    const uniqueSubCategories = [...new Set(products.map(p => {
      // Support des deux formats pour compatibilité
      return p.subCategory || p.subcategory || '';
    }))].sort();
    return uniqueSubCategories.filter(sc => sc); // Filtrer les valeurs null/undefined
  }, [products]);

  // Filtrer par marque ET sous-catégorie (les produits sont déjà filtrés par la recherche)
  const filteredProducts = useMemo(() => {
    // Les produits sont déjà filtrés par searchProducts, on retourne directement
    return products;
  }, [products]);

  // Vérifier si les badges de sous-catégories tiennent sur une ligne
  useEffect(() => {
    if (availableSubCategories.length === 0) {
      setShowSubCategoriesDropdown(true);
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!subCategoriesContainerRef.current) {
        return;
      }

      const container = subCategoriesContainerRef.current;
      const badges = container.querySelectorAll('button');
      
      if (badges.length === 0) {
        setShowSubCategoriesDropdown(true);
        return;
      }

      // Calculer la largeur totale nécessaire
      let totalWidth = 0;
      const positions = [];
      
      badges.forEach((badge, index) => {
        const rect = badge.getBoundingClientRect();
        positions.push(rect.top);
        totalWidth += rect.width;
        if (index < badges.length - 1) {
          totalWidth += 8; // gap de 8px
        }
      });

      // Obtenir la largeur disponible du conteneur parent (filters)
      const filtersContainer = container.closest('.product-search-filters');
      const availableWidth = filtersContainer 
        ? filtersContainer.getBoundingClientRect().width - 200 // Réserver 200px pour le dropdown marques
        : window.innerWidth - 600; // Fallback
      
      // Vérifier si tous les badges sont sur une seule ligne
      const uniquePositions = new Set(positions);
      const hasSingleLine = uniquePositions.size === 1;
      
      // Si la largeur totale dépasse l'espace disponible OU qu'ils sont sur plusieurs lignes, utiliser le dropdown
      const shouldUseDropdown = !hasSingleLine || totalWidth > availableWidth;
      
      console.log('🔍 [Badges] Check:', {
        totalWidth,
        availableWidth,
        hasSingleLine,
        uniquePositions: Array.from(uniquePositions),
        shouldUseDropdown
      });
      
      setShowSubCategoriesDropdown(shouldUseDropdown);
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [debouncedQuery, availableSubCategories, open]);

  // Restaurer l'état au montage (une seule fois)
  useEffect(() => {
    if (!hasRestoredStateRef.current && products.length > 0) {
      hasRestoredStateRef.current = true;
      // Si on a des produits sauvegardés, restaurer l'état ouvert
      if (open) {
        setOpen(true);
      }
    }
  }, []);

  // Sauvegarder l'état quand il change
  useEffect(() => {
    saveState({
      query,
      products,
      open,
      selectedBrand,
      availableBrands,
      selectedSubCategories
    });
  }, [query, products, open, selectedBrand, availableBrands, selectedSubCategories, saveState]);

  // Recherche produits côté client avec useMemo pour éviter les re-renders inutiles
  const searchResults = useMemo(() => {
    if (!allProducts || !debouncedQuery.trim()) {
      return [];
    }
    
    try {
      const results = searchProducts(allProducts, debouncedQuery, selectedBrand, selectedSubCategories);
      return results;
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  }, [allProducts, debouncedQuery, selectedBrand, selectedSubCategories]);

  // Mettre à jour les produits et les marques quand les résultats changent
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      // Si la query est vide, fermer les résultats
      if (searchResults.length === 0) {
        setOpen(false);
      }
        return;
      }

    // Mettre à jour les produits seulement si les résultats ont changé
    if (searchResults.length > 0 || debouncedQuery.trim()) {
      setProducts(searchResults);
        setOpen(true);

      // Mettre à jour les marques disponibles depuis les résultats
      const uniqueBrands = [...new Set(searchResults.map(p => p.brand))].sort();
        setAvailableBrands(['Toutes les marques', ...uniqueBrands]);
        
      // Ne réinitialiser les détails que si la query a vraiment changé (pas juste les filtres)
      // On garde les détails existants pour éviter les re-renders
    }
  }, [searchResults, debouncedQuery]);

  // Reset active index quand query change
  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedQuery]);

  // Navigation clavier
  function onKeyDown(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) setOpen(true);
    if (!products.length) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(a => (a + 1) % products.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(a => (a - 1 + products.length) % products.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const product = filteredProducts[activeIndex];
      if (product) {
        handleAddToCart(product);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      handleClose();
    }
  }

  // Initialiser les détails produits depuis les variantes (plus besoin d'appel API)
  // Utiliser useMemo pour éviter les re-renders inutiles
  useEffect(() => {
    if (filteredProducts.length > 0) {
      // Créer un batch de mises à jour pour éviter les re-renders multiples
      const updates = new Map();
      const variantUpdates = new Map();
      
      filteredProducts.forEach(product => {
        // Les produits chargés depuis CSV ont déjà toutes les variantes
        // On les mappe directement dans productsDetails
        if (!productsDetails.has(product.id)) {
          updates.set(product.id, {
            variants: product.variants.map(v => ({
              id: v.id,
              variant_key: v.id,
              sku: v.references.cabconnect || v.references.supplier || '',
              packaging: v.quantity || null, // Quantité depuis CSV (ex: "Boîte de 100")
              quantity: v.quantity || null, // Ajouter directement pour faciliter l'accès
              attributes: {
                size: v.size || null,
                color: v.color || null,
                price: (v.reducedPrice != null ? v.reducedPrice : v.referencePrice) || null,
                currency: 'EUR',
                quantity_per_pack: v.quantity || null // Alias pour compatibilité
              },
              variant_url: product.url || null
            })),
            aggregates: {
              avg_price: product.variants.length > 0 
                ? (product.variants[0].reducedPrice != null ? product.variants[0].reducedPrice : product.variants[0].referencePrice) || null
                : null
            },
            product: {
              referencePrice: product.variants[0]?.referencePrice ?? null,
              gacdReference: product.variants[0]?.references.cabconnect || null,
              gacdSourceUrl: product.url || null,
              fabricantReference: product.variants[0]?.references.brand || null,
              canonical_url: product.url || null,
              avg_price: product.variants[0]?.reducedPrice ?? product.variants[0]?.referencePrice ?? null
            },
            references: product.variants.length > 0 ? [{
              source: product.supplier,
              ref: product.variants[0].references.cabconnect || null,
              source_url: product.url || null
            }] : []
          });

          // Initialiser la variante par défaut
          if (product.variants.length > 0) {
            const firstVariant = product.variants[0];
            variantUpdates.set(product.id, {
              variantId: firstVariant.id,
              packaging: firstVariant.quantity || null,
              attributes: {
                size: firstVariant.size || null,
                color: firstVariant.color || null,
                price: (firstVariant.reducedPrice != null ? firstVariant.reducedPrice : firstVariant.referencePrice) || null
              },
              variant_url: product.url || null
            });
          }
        }
      });

      // Appliquer toutes les mises à jour en une seule fois
      if (updates.size > 0) {
        setProductsDetails(prev => {
          const newMap = new Map(prev);
          updates.forEach((value, key) => {
            newMap.set(key, value);
          });
          return newMap;
        });
      }

      if (variantUpdates.size > 0) {
        setSelectedVariants(prev => {
          const newMap = new Map(prev);
          variantUpdates.forEach((value, key) => {
            if (!newMap.has(key)) {
              newMap.set(key, value);
            }
          });
          return newMap;
        });
      }
    }
  }, [filteredProducts]); // Retirer productsDetails des dépendances pour éviter la boucle

  // Calculer prix unitaire
  const calculateUnitPrice = (product) => {
    if (!product.avg_price) return Infinity;
    // Pour l'instant on utilise avg_price tel quel
    // TODO: parser la quantité depuis les attributs si nécessaire
    return product.avg_price;
  };

  // Trouver le moins cher par sous-catégorie
  const cheapestBySubCategory = useMemo(() => {
    if (filteredProducts.length === 0) return new Map();
    return findCheapestBySubCategory(filteredProducts);
  }, [filteredProducts]);

  // Ajouter au panier avec sélection spécifique
  const handleAddToCartWithSelection = (product, selection) => {
    const details = productsDetails.get(product.id);
    const variants = details?.variants || [];
    
    // Trouver la variante correspondante en incluant le packaging
    const matchingVariant = variants.find(v => {
      const sizeMatch = !selection.size || (v.attributes?.size || v.size) === selection.size;
      const colorMatch = !selection.color || (v.attributes?.color || v.color) === selection.color;
      const packagingMatch = !selection.packaging || 
        v.packaging === selection.packaging || 
        v.attributes?.quantity_per_pack === selection.packaging ||
        v.quantity === selection.packaging;
      return sizeMatch && colorMatch && packagingMatch;
    }) || variants[0];
    
    // Extraire la référence GACD
    const gacdReference = details?.product?.gacdReference || details?.references?.find(ref => ref.source === "GACD")?.ref || null;
    
    const cartItem = {
      id: product.id,
      variantId: matchingVariant?.id || null,
      name: product.title,
      brand: product.brand,
      category: product.category,
      subcategory: product.subcategory,
      price: matchingVariant?.attributes?.price || details?.aggregates?.avg_price || product.avg_price || 0,
      quantity: selection.quantity || 1,
      thumbnail_url: product.thumbnail_url,
      packaging: selection.packaging || matchingVariant?.packaging || matchingVariant?.quantity || null,
      variant_attributes: {
        ...(selection.size ? { size: selection.size } : {}),
        ...(selection.color ? { color: selection.color } : {}),
        ...(selection.packaging ? { packaging: selection.packaging } : {}),
        ...(matchingVariant?.attributes || {})
      },
      variant_url: matchingVariant?.variant_url || details?.product?.canonical_url || product.canonical_url || null,
      gacdReference: gacdReference
    };
    
    setCart(prev => {
      // Vérifier si le même produit avec la même variante existe déjà
      const existingIndex = prev.findIndex(item => 
        item.id === product.id && 
        item.variantId === cartItem.variantId &&
        item.variant_attributes?.size === cartItem.variant_attributes?.size &&
        item.variant_attributes?.color === cartItem.variant_attributes?.color
      );
      if (existingIndex !== -1) {
        // Remplacer au lieu d'additionner
        return prev.map((item, index) => 
          index === existingIndex
            ? { ...cartItem } // Remplacer par la nouvelle sélection avec la quantité choisie
            : item
        );
      }
      return [...prev, cartItem];
    });
  };

  // Ajouter au panier (ancien système - gardé pour compatibilité)
  const handleAddToCart = (product, e) => {
    if (e) e.stopPropagation();
    
    const productKey = product.id;
    const productSelections = Array.isArray(selectedOptions[productKey]) ? selectedOptions[productKey] : [];
    
    // Si des sélections existent, utiliser le nouveau système
    if (productSelections.length > 0) {
      productSelections.forEach(selection => {
        handleAddToCartWithSelection(product, selection);
      });
      return;
    }
    
    // Sinon, utiliser l'ancien système
    const selected = selectedVariants.get(product.id);
    const details = productsDetails.get(product.id);
    
    const cartItem = {
      id: product.id,
      variantId: selected?.variantId || null,
      name: product.title,
      brand: product.brand,
      category: product.category,
      subcategory: product.subcategory,
      price: product.avg_price || 0,
      quantity: 1,
      thumbnail_url: product.thumbnail_url,
      packaging: selected?.packaging || null,
      variant_attributes: selected?.attributes || {},
      variant_url: selected?.variant_url || details?.product?.canonical_url || product.canonical_url || null
    };
    
    setCart(prev => {
      const existingIndex = prev.findIndex(item => 
        item.id === product.id && 
        item.variantId === cartItem.variantId
      );
      if (existingIndex !== -1) {
        // Remplacer au lieu d'additionner
        return prev.map((item, index) => 
          index === existingIndex
            ? { ...cartItem } // Remplacer par la nouvelle sélection
            : item
        );
      }
      return [...prev, cartItem];
    });
  };

  // Toast notification (avec gestion d'erreur robuste)
  const showToast = (message) => {
    const toast = document.createElement('div');
    toast.className = 'product-search-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.classList.add('show');
    }, 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        // Vérifier que l'élément existe avant de le supprimer
        if (toast && toast.parentNode === document.body) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 2000);
  };

  // Calculer total du panier
  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [cart]);

  // Charger le cabinet et le compte distributeur pour le formulaire email
  const loadCabinetAndAccount = useCallback(async () => {
    setLoadingCabinet(true);
    try {
      // Charger le cabinet
      const cabinetResponse = await fetch(API_ENDPOINTS.CABINETS_LIST(), {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (cabinetResponse.ok) {
        const cabinetData = await cabinetResponse.json();
        const currentCabinet = Array.isArray(cabinetData) 
          ? cabinetData[0] 
          : (cabinetData.data?.[0] || cabinetData);
        
        if (currentCabinet) {
          setCabinet(currentCabinet);
          
          // Pré-remplir les emails si disponible
          setEmailForm(prev => ({
            ...prev,
            recipientEmail: currentCabinet.meta?.contact_email || '',
            senderEmail: currentCabinet.meta?.sender_email || ''
          }));
        }
      }

      // Charger le compte distributeur pour GACD
      const accountResponse = await fetch(API_ENDPOINTS.DISTRIBUTOR_ACCOUNT_BY_CODE('GACD'), {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (accountResponse.ok) {
        const accountData = await accountResponse.json();
        const account = Array.isArray(accountData) 
          ? accountData[0] 
          : (accountData.data || accountData);
        
        if (account) {
          setDistributorAccount(account);
          setEmailForm(prev => ({
            ...prev,
            accountNumber: account.account_number || ''
          }));
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement du cabinet/compte:', error);
    } finally {
      setLoadingCabinet(false);
    }
  }, []);

  // Générer le texte du mail avec les produits du panier
  const generateEmailBody = useCallback((cartItems) => {
    const itemsText = cartItems.map(item => {
      // Utiliser la référence GACD stockée dans l'item ou chercher dans les détails
      const gacdRef = item.gacdReference || productsDetails.get(item.id)?.product?.gacdReference || productsDetails.get(item.id)?.references?.find(ref => ref.source === "GACD")?.ref;
      const productName = item.name;
      const quantity = item.quantity;
      const variantInfo = [];
      
      if (item.variant_attributes?.size) {
        variantInfo.push(`Taille ${item.variant_attributes.size}`);
      }
      if (item.variant_attributes?.color) {
        variantInfo.push(`Couleur ${item.variant_attributes.color}`);
      }
      
      const variantStr = variantInfo.length > 0 ? ` (${variantInfo.join(', ')})` : '';
      const refStr = gacdRef ? ` - Réf. GACD: ${gacdRef}` : '';
      
      return `[${productName}${variantStr}${refStr}] – [${quantity}]`;
    }).join('\n');

    const cabinetName = cabinet?.raison_sociale || '[Nom du cabinet]';
    const addressL1 = cabinet?.adresse?.l1 || cabinet?.adresse_l1 || '[Adresse ligne 1]';
    const addressL2 = cabinet?.adresse?.l2 || cabinet?.adresse_l2 || '';
    const fullAddress = addressL2 ? `${addressL1}\n${addressL2}` : addressL1;
    const postalCode = cabinet?.adresse?.code_postal || cabinet?.code_postal || '[Code postal]';
    const city = cabinet?.adresse?.ville || cabinet?.ville || '[Ville]';

    return `Bonjour,

Je vous écris afin de passer commande pour le matériel dentaire suivant :
${itemsText}

La livraison est à effectuer à l'adresse suivante :
${cabinetName}
${fullAddress}
${postalCode}, ${city}

Merci également de joindre le devis ou la facture pro forma correspondante pour validation.
Dans l'attente de votre confirmation,
je vous remercie par avance pour votre réactivité et votre professionnalisme.
Bien cordialement,`;
  }, [cabinet, productsDetails]);

  // Mettre à jour le texte du mail quand le panier change
  useEffect(() => {
    if (showEmailForm && cart.length > 0 && cabinet) {
      const emailBody = generateEmailBody(cart);
      setEmailForm(prev => ({
        ...prev,
        body: emailBody
      }));
    }
  }, [cart, showEmailForm, cabinet, generateEmailBody]);

  // Réinitialiser marque si pas disponible
  useEffect(() => {
    if (!availableBrands.includes(selectedBrand)) {
      setSelectedBrand('Toutes les marques');
    }
  }, [availableBrands, selectedBrand]);

  // Réinitialiser les sous-catégories sélectionnées si elles ne sont plus disponibles
  useEffect(() => {
    setSelectedSubCategories(prev => 
      prev.filter(subCat => availableSubCategories.includes(subCat))
    );
  }, [availableSubCategories]);

  // Sélectionner automatiquement les options uniques (une seule option disponible) pour chaque produit
  useEffect(() => {
    if (!allProducts || productsDetails.size === 0) return;

    const updates = {};
    let hasUpdates = false;

    allProducts.forEach(product => {
      const productKey = product.id;
      const productSelections = Array.isArray(selectedOptions[productKey]) ? selectedOptions[productKey] : [];
      
      // Ne pas écraser les sélections existantes
      if (productSelections.length > 0) return;

      const details = productsDetails.get(product.id);
      if (!details || !details.variants || details.variants.length === 0) return;

      const variants = details.variants;
      
      // Extraire les options disponibles
      const sizes = new Set();
      const packagings = new Set();
      const colors = new Set();
      
      variants.forEach(v => {
        const sizeValue = v.attributes?.size || v.size;
        if (sizeValue) sizes.add(sizeValue);
        
        const colorValue = v.attributes?.color || v.color;
        if (colorValue) colors.add(colorValue);
        
        const packagingValue = v.packaging || v.quantity || v.attributes?.quantity_per_pack;
        if (packagingValue) packagings.add(packagingValue);
      });

      const sizesArray = Array.from(sizes);
      const colorsArray = Array.from(colors);
      const packagingsArray = Array.from(packagings);

      const hasMultipleSizes = sizesArray.length > 1;
      const hasMultipleColors = colorsArray.length > 1;
      const hasMultiplePackagings = packagingsArray.length > 1;

      // Si toutes les options sont uniques, créer une sélection automatique complète
      if (!hasMultipleSizes && !hasMultipleColors && !hasMultiplePackagings) {
        const autoSelection = {
          size: sizesArray[0] || null,
          color: colorsArray[0] || null,
          packaging: packagingsArray[0] || null,
          quantity: 1
        };
        
        updates[productKey] = [autoSelection];
        hasUpdates = true;
      }
    });

    // Appliquer toutes les mises à jour en une seule fois
    if (hasUpdates) {
      setSelectedOptions(prev => {
        const newOptions = { ...prev };
        Object.keys(updates).forEach(key => {
          // Ne mettre à jour que si aucune sélection n'existe déjà
          if (!Array.isArray(newOptions[key]) || newOptions[key].length === 0) {
            newOptions[key] = updates[key];
          }
        });
        return newOptions;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProducts, productsDetails]);

  // Fermer le menu déroulant des sous-catégories quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subCategoriesContainerRef.current && !subCategoriesContainerRef.current.contains(event.target)) {
        setIsSubCategoriesDropdownOpen(false);
      }
    };

    if (isSubCategoriesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSubCategoriesDropdownOpen]);

  // Highlight texte
  const highlight = (text, q) => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="highlight">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  // Fonction pour fermer le modal et effacer le panier non validé
  const handleClose = useCallback(() => {
    // Effacer le panier non validé
    setCart([]);
    // Réinitialiser les sélections
    setSelectedOptions({});
    setSelectedVariants(new Map());
    // Effacer le mot de recherche
    setQuery('');
    // Réafficher le moteur de recherche (fermer le formulaire email si ouvert)
    setShowEmailForm(false);
    // Fermer le modal
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  // Afficher un loader pendant le chargement initial des produits
  if (isLoadingProducts) {
    return (
      <div className="product-search-overlay" onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}>
        <div className="product-search-modal" onClick={(e) => e.stopPropagation()}>
          <div className="product-search-loading">
            <div className="product-search-spinner" />
            <p>Chargement du catalogue produits...</p>
          </div>
        </div>
      </div>
    );
  }

  // Afficher une erreur si le chargement a échoué
  if (loadError) {
    return (
      <div className="product-search-overlay" onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}>
        <div className="product-search-modal" onClick={(e) => e.stopPropagation()}>
          <div className="product-search-error">
            <div className="product-search-error-icon">⚠️</div>
            <p>{loadError}</p>
            <button
              className="product-search-error-retry"
              onClick={() => {
                setLoadError(null);
                setIsLoadingProducts(true);
                // Recharger
                loadGacdProductsFromCsv()
                  .then(data => {
                    setAllProducts(data);
                    setIsLoadingProducts(false);
                  })
                  .catch(err => {
                    console.error('Erreur lors du rechargement:', err);
                    setLoadError('Impossible de charger le catalogue produits. Veuillez réessayer.');
                    setIsLoadingProducts(false);
                  });
              }}
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-search-overlay" onClick={(e) => {
      // Seulement fermer si on clique directement sur l'overlay, pas sur la modal
      if (e.target === e.currentTarget) {
        handleClose();
      }
    }}>
      <div className="product-search-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header - caché seulement si formulaire affiché */}
        {!showEmailForm && (
        <div className="product-search-header">
          <div className="product-search-title-group">
            <div className="product-search-logo">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div>
              <h2 className="product-search-title">Recherche de produits</h2>
              <p className="product-search-subtitle">Catalogue professionnel</p>
            </div>
          </div>
          <button className="product-search-close" onClick={handleClose}>
            ✕
          </button>
        </div>
        )}

        {/* Search box - cachée seulement si formulaire affiché */}
        {!showEmailForm && (
        <div className="product-search-box">
          <div className="product-search-input-wrapper">
            <svg className="product-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => {
                const newQuery = e.target.value;
                setQuery(newQuery);
                // Si la nouvelle query est différente, ouvrir les résultats
                // Si on efface complètement, garder les résultats existants mais fermer la liste
                if (newQuery.trim()) {
                setOpen(true);
                }
              }}
              onFocus={() => setOpen(!!query)}
              onBlur={(e) => {
                // Ne pas fermer automatiquement, garder les résultats affichés
                // Seulement fermer si on clique vraiment ailleurs (géré par onClick sur overlay)
                setTimeout(() => {
                  if (!brandDropdownRef.current?.contains(e.relatedTarget)) {
                    // Ne pas fermer si on a des résultats
                    // setOpen(false); // Commenté pour garder les résultats
                  }
                }, 150);
              }}
              onKeyDown={onKeyDown}
              placeholder="Recherchez gants, masques, fraises, implants..."
              className="product-search-input"
              aria-autocomplete="list"
              aria-expanded={open}
            />
            {query && (
              <button className="product-search-clear" onClick={() => setQuery('')}>
                ✕
              </button>
            )}
          </div>

          {/* Dropdown marques et sous-catégories */}
          {open && products.length > 0 && (
            <div className="product-search-filters" data-filter-container>
              <div className="product-search-filter-group" ref={brandDropdownRef}>
                <button
                  type="button"
                  className="product-search-dropdown-btn"
                  onClick={() => setIsBrandDropdownOpen(!isBrandDropdownOpen)}
                >
                  <span>{selectedBrand}</span>
                  <svg 
                    className={`product-search-chevron ${isBrandDropdownOpen ? 'open' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isBrandDropdownOpen && (
                  <div className="product-search-dropdown-menu">
                    {availableBrands.map(brand => (
                      <button
                        key={brand}
                        type="button"
                        className={`product-search-dropdown-item ${selectedBrand === brand ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedBrand(brand);
                          setIsBrandDropdownOpen(false);
                        }}
                      >
                        {selectedBrand === brand && <span className="product-search-check">✓</span>}
                        {brand}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Badges ou menu déroulant de sous-catégories */}
              {availableSubCategories.length > 0 && (
                <>
                  {/* Badges masqués pour la mesure */}
                  <div 
                    className="product-search-subcategories-measure"
                    ref={subCategoriesContainerRef}
                  >
                    {availableSubCategories.map((subCat) => (
                      <button
                        key={subCat}
                        type="button"
                        className="product-search-subcategory-badge"
                      >
                        {subCat}
                      </button>
                    ))}
                  </div>

                  {/* Menu déroulant ou badges selon le résultat de la mesure */}
                  {showSubCategoriesDropdown ? (
                    <div className="product-search-filter-group" ref={subCategoriesDropdownRef}>
                      <button
                        type="button"
                        className="product-search-dropdown-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSubCategoriesDropdownOpen(!isSubCategoriesDropdownOpen);
                        }}
                      >
                        <span>
                          {selectedSubCategories.length === 0 
                            ? 'Toutes les catégories' 
                            : selectedSubCategories.length === 1
                            ? selectedSubCategories[0]
                            : `${selectedSubCategories.length} catégories`}
                        </span>
                        <svg 
                          className={`product-search-chevron ${isSubCategoriesDropdownOpen ? 'open' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isSubCategoriesDropdownOpen && (
                        <div className="product-search-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className={`product-search-dropdown-item ${selectedSubCategories.length === 0 ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSubCategories([]);
                              setIsSubCategoriesDropdownOpen(false);
                            }}
                          >
                            {selectedSubCategories.length === 0 && <span className="product-search-check">✓</span>}
                            Toutes les catégories
                          </button>
                          {availableSubCategories.map(subCat => {
                            const isSelected = selectedSubCategories.includes(subCat);
                            return (
                              <button
                                key={subCat}
                                type="button"
                                className={`product-search-dropdown-item ${isSelected ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSubCategories(prev => 
                                    isSelected 
                                      ? prev.filter(sc => sc !== subCat)
                                      : [...prev, subCat]
                                  );
                                  // Ne pas fermer le menu pour permettre plusieurs sélections
                                }}
                              >
                                {isSelected && <span className="product-search-check">✓</span>}
                                {subCat}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="product-search-subcategories-badges-container">
                      {availableSubCategories.map((subCat) => {
                        const isSelected = selectedSubCategories.includes(subCat);
                        return (
                          <button
                            key={subCat}
                            type="button"
                            className={`product-search-subcategory-badge ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedSubCategories(prev => 
                                isSelected 
                                  ? prev.filter(sc => sc !== subCat)
                                  : [...prev, subCat]
                              );
                            }}
                          >
                            {subCat}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        )}

        {/* Main Content: Products + Cart ou Email Form */}
        <div className="product-search-main-content">
          {/* Email Form - Left Column (quand on valide le panier) */}
          {showEmailForm && cart.length > 0 && (
            <div className="product-search-email-form">
              {loadingCabinet && (
                <div className="product-search-email-form-loading">
                  Chargement des informations...
                </div>
              )}
              
              <div className="product-search-email-form-content">
                {/* Ligne 1: Objet + Numéro de compte */}
                <div className="product-search-email-form-row">
                  <div className="product-search-email-form-field">
                    <label>Objet :</label>
                    <input
                      type="text"
                      value={emailForm.subject}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                      className="product-search-email-form-input"
                    />
                  </div>
                  <div className="product-search-email-form-field">
                    <label>Numéro de compte : {distributorAccount?.account_number ? '(GACD)' : '(optionnel)'}</label>
                    <input
                      type="text"
                      value={emailForm.accountNumber}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, accountNumber: e.target.value }))}
                      className="product-search-email-form-input"
                      placeholder={distributorAccount?.account_number || "Saisir le numéro de compte"}
                    />
                  </div>
                </div>

                {/* Ligne 2: Raison sociale + Adresse */}
                <div className="product-search-email-form-row">
                  <div className="product-search-email-form-field">
                    <label>Raison sociale :</label>
                    <input
                      type="text"
                      value={cabinet?.raison_sociale || ''}
                      disabled
                      className="product-search-email-form-input product-search-email-form-input-disabled"
                    />
                  </div>
                  <div className="product-search-email-form-field">
                    <label>Adresse :</label>
                    <input
                      type="text"
                      value={[
                        cabinet?.adresse?.l1 || cabinet?.adresse_l1 || '',
                        cabinet?.adresse?.l2 || cabinet?.adresse_l2 || ''
                      ].filter(Boolean).join(', ')}
                      disabled
                      className="product-search-email-form-input product-search-email-form-input-disabled"
                    />
                  </div>
                </div>

                {/* Ligne 3: Code postal + Ville */}
                <div className="product-search-email-form-row">
                  <div className="product-search-email-form-field">
                    <label>Code postal :</label>
                    <input
                      type="text"
                      value={cabinet?.adresse?.code_postal || cabinet?.code_postal || ''}
                      disabled
                      className="product-search-email-form-input product-search-email-form-input-disabled"
                    />
                  </div>
                  <div className="product-search-email-form-field">
                    <label>Ville :</label>
                    <input
                      type="text"
                      value={cabinet?.adresse?.ville || cabinet?.ville || ''}
                      disabled
                      className="product-search-email-form-input product-search-email-form-input-disabled"
                    />
                  </div>
                </div>

                {/* Ligne 4: Email destinataire + Email expéditeur */}
                <div className="product-search-email-form-row">
                  <div className="product-search-email-form-field">
                    <label>Email destinataire :</label>
                    <input
                      type="email"
                      value={emailForm.recipientEmail}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, recipientEmail: e.target.value }))}
                      className="product-search-email-form-input"
                      placeholder="email@destinataire.com"
                    />
                  </div>
                  <div className="product-search-email-form-field">
                    <label>Email expéditeur :</label>
                    <input
                      type="email"
                      value={emailForm.senderEmail}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, senderEmail: e.target.value }))}
                      className="product-search-email-form-input"
                      placeholder="email@expediteur.com"
                    />
                  </div>
                </div>

                {/* Corps du message */}
                <div className="product-search-email-form-message">
                  <label>Message :</label>
                  <textarea
                    value={emailForm.body}
                    onChange={(e) => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                    className="product-search-email-form-textarea"
                  />
                </div>

                {/* Actions */}
                <div className="product-search-email-form-actions">
                  <button
                    className="product-search-email-form-btn product-search-email-form-btn-secondary"
                    onClick={() => setShowEmailForm(false)}
                  >
                    Annuler
                  </button>
                  <button
                    className="product-search-email-form-btn product-search-email-form-btn-primary"
                    onClick={() => {
                      // Créer un lien mailto
                      const mailtoLink = `mailto:${emailForm.recipientEmail}?subject=${encodeURIComponent(emailForm.subject)}&body=${encodeURIComponent(emailForm.body)}${emailForm.senderEmail ? `&cc=${encodeURIComponent(emailForm.senderEmail)}` : ''}`;
                      window.location.href = mailtoLink;
                    }}
                    disabled={!emailForm.recipientEmail || !emailForm.body}
                  >
                    Envoyer la commande
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results - Left Column (si pas de formulaire email) */}
          {!showEmailForm && open && (filteredProducts.length > 0 || (products.length > 0 && selectedSubCategories.length > 0)) && (
          <div className="product-search-results">
            <ul ref={listRef} className="product-search-list">
              {filteredProducts.map((p, i) => {
                const productSubCat = p.subCategory || p.subcategory || '';
                const isCheapest = cheapestBySubCategory.get(productSubCat)?.id === p.id;
                return (
                  <li
                    key={p.id}
                    className={`product-search-item ${activeIndex === i ? 'active' : ''} ${isCheapest ? 'cheapest' : ''}`}
                    onMouseEnter={() => setActiveIndex(i)}
                  >
                    {isCheapest && (
                      <div className="product-search-badge-cheapest">
                        ⭐ Produit le moins cher
                      </div>
                    )}
                    
                    <div className="product-search-item-content">
                      {/* Structure exacte page.tsx: px-6 py-4 = padding interne */}
                      <div className="product-search-item-inner">
                        {/* Header exact comme page.tsx: flex items-start justify-between gap-3 mb-2 */}
                        <div className="product-search-item-header-flex">
                          <div className="product-search-item-header-left">
                            {/* Title exact: font-bold text-gray-900 text-lg lg:text-xl mb-2 */}
                          <h3 className="product-search-item-title">
                            {highlight(p.name || p.title || '', debouncedQuery)}
                          </h3>
                            {/* Meta exact: flex items-center gap-2 flex-wrap */}
                          <div className="product-search-item-meta">
                              {/* Brand exact: inline-block px-2 py-1 rounded-full bg-blue-600 text-white font-semibold text-xs */}
                            <span className="product-search-item-brand">
                                {highlight(p.brand || '', debouncedQuery)}
                            </span>
                              {/* Category exact: text-gray-600 text-xs */}
                            <span className="product-search-item-category">
                                {highlight(p.subCategory || p.subcategory || '', debouncedQuery)}
                            </span>
                          </div>
                        </div>
                          {/* Price section exact: text-right */}
                        <div className="product-search-item-header-right">
                          {(() => {
                            const details = productsDetails.get(p.id);
                            const selected = selectedVariants.get(p.id);
                            
                            // Prix: utiliser aggregates.avg_price OU référence produit OU première variante
                            let priceToShow = null;
                            if (details) {
                              // Priorité 1: Prix de la variante sélectionnée
                              if (selected?.attributes?.price) {
                                priceToShow = selected.attributes.price;
                              }
                              // Priorité 2: Agrégats avg_price
                              else if (details.aggregates?.avg_price) {
                                priceToShow = details.aggregates.avg_price;
                              }
                              // Priorité 3: Prix de référence du produit
                              else if (details.product?.referencePrice) {
                                priceToShow = details.product.referencePrice;
                              }
                              // Priorité 4: Première variante
                              else if (details.variants?.[0]?.attributes?.price) {
                                priceToShow = details.variants[0].attributes.price;
                              }
                              // Priorité 5: Prix du produit initial (fallback)
                              else if (p.variants?.[0]?.reducedPrice ?? p.variants?.[0]?.referencePrice) {
                                priceToShow = p.variants[0].reducedPrice ?? p.variants[0].referencePrice;
                              }
                            } else if (p.variants?.[0]?.reducedPrice ?? p.variants?.[0]?.referencePrice) {
                              // Fallback si détails pas encore chargés
                              priceToShow = p.variants[0].reducedPrice ?? p.variants[0].referencePrice;
                            }
                            
                            // URL selon guide: product.canonical_url RECOMMANDÉ, puis references[].source_url GACD
                            // Priorité:
                            // 1. variant_url de la variante sélectionnée (si disponible)
                            // 2. product.canonical_url (RECOMMANDÉ - URL principale du produit)
                            // 3. source_url depuis référence GACD (fallback)
                            let url = null;
                            if (selected?.variant_url) {
                              // Variante a sa propre URL
                              url = selected.variant_url;
                            } else if (details?.product?.canonical_url) {
                              // ✅ RECOMMANDÉ: canonical_url du produit
                              url = details.product.canonical_url;
                            } else if (p.canonical_url) {
                              // canonical_url du produit initial (si détails pas encore chargés)
                              url = p.canonical_url;
                            } else if (details?.product?.gacdSourceUrl) {
                              // Fallback: source_url depuis référence GACD
                              url = details.product.gacdSourceUrl;
                            } else if (details?.references) {
                              // Fallback: chercher source_url dans les références GACD
                              const gacdRef = details.references.find(ref => ref.source === "GACD");
                              if (gacdRef?.source_url) {
                                url = gacdRef.source_url;
                              }
                            } else if (p.source_urls && Array.isArray(p.source_urls) && p.source_urls.length > 0) {
                              // Dernier fallback: source_urls du produit initial
                              url = p.source_urls[0];
                            }
                            
                            return priceToShow ? (
                              <>
                                {/* Price exact: text-xl lg:text-2xl font-bold text-blue-600 */}
                            <span className="product-search-price-amount">
                                  {priceToShow.toFixed(2)}€
                            </span>
                                {/* URL button exact comme page.tsx */}
                                {url && (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="product-search-url-btn"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Voir sur le site du fournisseur"
                                  >
                                    <svg className="w-4 h-4 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                )}
                              </>
                            ) : (
                              <span className="product-search-price-na">Prix non disponible</span>
                            );
                          })()}
                        </div>
                      </div>
                      
                      {/* Séparateur exact comme page.tsx: border-t border-gray-200 my-2 */}
                      <div className="product-search-separator"></div>
                      
                      {/* Options: Taille, Couleur, Quantité - Structure exacte page.tsx: flex flex-col gap-3 */}
                        {(() => {
                          const details = productsDetails.get(p.id);
                          const variants = details?.variants || [];
                          const productKey = p.id;
                          const productSelections = Array.isArray(selectedOptions[productKey]) ? selectedOptions[productKey] : [];
                          
                          if (loadingDetails.has(p.id)) {
                            return <div className="product-search-variants-loading">Chargement des variantes...</div>;
                          }

                          if (variants.length === 0) {
                            return null;
                          }

                        // Extraire les tailles, quantités et couleurs disponibles depuis les variantes
                        // Structure selon doc API: v.attributes.size, v.attributes.color, v.packaging
                        // Mais aussi depuis les données CSV: v.quantity (directement depuis ProductVariant)
                        const sizes = new Set();
                        const packagings = new Set();
                        const colors = new Map();
                        
                        variants.forEach(v => {
                          // Taille depuis attributes.size OU directement depuis variant.size (CSV)
                          const sizeValue = v.attributes?.size || v.size;
                          if (sizeValue) {
                            sizes.add(sizeValue);
                          }
                          
                          // Packaging/Quantité: v.packaging OU attributes.quantity_per_pack OU v.quantity (CSV)
                          if (v.packaging) {
                            packagings.add(v.packaging);
                          }
                          if (v.attributes?.quantity_per_pack) {
                            packagings.add(v.attributes.quantity_per_pack);
                          }
                          // Quantité depuis les données CSV (ProductVariant.quantity)
                          if (v.quantity) {
                            packagings.add(v.quantity);
                          }
                          
                          // Couleur depuis attributes.color OU directement depuis variant.color (CSV)
                          const colorValue = v.attributes?.color || v.color;
                          if (colorValue) {
                            const colorName = String(colorValue).trim();
                            if (colorName) {
                              const colorHex = getColorHex(colorName);
                              colors.set(colorName, colorHex);
                            }
                          }
                        });

                          const sizesArray = Array.from(sizes).sort();
                          const packagingsArray = Array.from(packagings).sort((a, b) => {
                            const numA = parseInt(String(a).replace(/\D/g, '')) || 0;
                            const numB = parseInt(String(b).replace(/\D/g, '')) || 0;
                            return numA - numB;
                          });
                          const colorsArray = Array.from(colors.entries());

                          return (
                            <>
                              <div className="product-search-options-container">
                                {/* Tailles - Premier comme page.tsx: flex items-center gap-2 flex-wrap */}
                                {sizesArray.length > 0 && (
                                  <div className="product-search-options-row">
                                    <span className="product-search-options-label">Taille{sizesArray.length > 1 ? 's' : ''}:</span>
                                    <div className="product-search-options-buttons">
                                      {sizesArray.map(size => {
                                        const isSelected = productSelections.some(sel => sel.size === size);
                                        return (
                                          <button
                                            key={size}
                                            type="button"
                                            className={`product-search-option-btn ${isSelected ? 'selected' : ''}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedOptions(prev => {
                                                const currentSelections = Array.isArray(prev[productKey]) ? prev[productKey] : [];
                                                if (isSelected) {
                                                  // Retirer la sélection
                                                  return {
                                                    ...prev,
                                                    [productKey]: currentSelections.filter(sel => sel.size !== size)
                                                  };
                                                } else {
                                                  // Ajouter une nouvelle sélection avec la taille
                                                  // Si une seule option de packaging existe, l'ajouter automatiquement
                                                  const defaultPackaging = packagingsArray.length === 1 ? packagingsArray[0] : null;
                                                  return {
                                                    ...prev,
                                                    [productKey]: [...currentSelections, { size, packaging: defaultPackaging, quantity: 1 }]
                                                  };
                                                }
                                              });
                                            }}
                                          >
                                            {size}
                                          </button>
                                        );
                                      })}
                                    </div>
                        </div>
                      )}
                      
                                {/* Couleurs - Deuxième comme page.tsx */}
                                {colorsArray.length > 0 && (
                                  <div className="product-search-options-row">
                                    <span className="product-search-options-label">Couleur{colorsArray.length > 1 ? 's' : ''}:</span>
                                    <div className="product-search-options-buttons">
                                      {colorsArray.map(([colorName, colorHex]) => {
                                        const isSelected = productSelections.some(sel => sel.color === colorName);
                                        return (
                                          <button
                                            key={colorName}
                                            type="button"
                                            className={`product-search-option-btn ${isSelected ? 'selected' : ''}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedOptions(prev => {
                                                const currentSelections = Array.isArray(prev[productKey]) ? prev[productKey] : [];
                                                
                                                if (isSelected) {
                                                  // Retirer cette couleur de toutes les sélections
                                                  return {
                                                    ...prev,
                                                    [productKey]: currentSelections.filter(sel => sel.color !== colorName)
                                                  };
                                                } else {
                                                  // Ajouter une nouvelle sélection avec cette couleur
                                                  // Si une seule option de packaging existe, l'ajouter automatiquement
                                                  const defaultPackaging = packagingsArray.length === 1 ? packagingsArray[0] : null;
                                                  const newSelection = { color: colorName, packaging: defaultPackaging, quantity: 1 };
                                                  return {
                                                    ...prev,
                                                    [productKey]: [...currentSelections, newSelection]
                                                  };
                                                }
                                              });
                                            }}
                                          >
                                            {formatColorName(colorName)}
                                          </button>
                );
              })}
                        </div>
                      </div>
        )}

                                {/* Quantité/Conditionnement - Dernier comme page.tsx */}
                                {packagingsArray.length > 0 && (
                                  <div className="product-search-options-row">
                                    <span className="product-search-options-label">Conditionnement{packagingsArray.length > 1 ? 's' : ''}:</span>
                                    <div className="product-search-options-buttons">
                                      {packagingsArray.map(packaging => {
                                        const isSelected = productSelections.some(sel => sel.packaging === packaging);
                                        return (
                                          <button
                                            key={packaging}
                                            type="button"
                                            className={`product-search-option-btn ${isSelected ? 'selected' : ''}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedOptions(prev => {
                                                const currentSelections = Array.isArray(prev[productKey]) ? prev[productKey] : [];
                                                
                                                if (isSelected) {
                                                  // Retirer cette quantité de toutes les sélections
                                                  return {
                                                    ...prev,
                                                    [productKey]: currentSelections.filter(sel => sel.packaging !== packaging)
                                                  };
                                                } else {
                                                  // Ajouter une nouvelle sélection avec cette quantité
                                                  const newSelection = { packaging, quantity: 1 };
                                                  return {
                                                    ...prev,
                                                    [productKey]: [...currentSelections, newSelection]
                                                  };
                                                }
                                              });
                                            }}
                                          >
                                            {packaging}
                                          </button>
                                        );
                                      })}
                                    </div>
                        </div>
                      )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      
                      {/* Sélections avec boutons +/- - affiché même si pas de variantes */}
                      {(() => {
                        const productKey = p.id;
                        const productSelections = Array.isArray(selectedOptions[productKey]) ? selectedOptions[productKey] : [];
                        
                        if (productSelections.length === 0) {
                          return null;
                        }
                        
                        return (
                          <div className="product-search-selections-container">
                            {productSelections.map((selection, selectionIndex) => {
                              const quantity = selection.quantity || 1;
                              return (
                                <div key={selectionIndex} className="product-search-selection-item">
                                  <div className="product-search-selection-info">
                                    <span className="product-search-selection-label">Sélection:</span>
                                    {selection.size && (
                                      <span className="product-search-selection-size">Taille {selection.size}</span>
                                    )}
                                    {selection.color && (
                                      <span className="product-search-selection-color">Couleur {selection.color}</span>
                                    )}
                                    {selection.packaging && (
                                      <span className="product-search-selection-packaging">Conditionnement: {selection.packaging}</span>
                                    )}
                                  </div>
                                  <div className="product-search-selection-controls">
                                    <button
                                      type="button"
                                      className="product-search-qty-btn product-search-qty-btn-minus"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (quantity > 1) {
                                          setSelectedOptions(prev => ({
                                            ...prev,
                                            [productKey]: (Array.isArray(prev[productKey]) ? prev[productKey] : []).map((sel, idx) => 
                                              idx === selectionIndex 
                                                ? { ...sel, quantity: quantity - 1 }
                                                : sel
                                            )
                                          }));
                                        }
                                      }}
                                      disabled={quantity <= 1}
                                    >
                                      −
                                    </button>
                                    <span className="product-search-qty-display">{quantity}</span>
                                    <button
                                      type="button"
                                      className="product-search-qty-btn product-search-qty-btn-plus"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedOptions(prev => ({
                                          ...prev,
                                          [productKey]: (Array.isArray(prev[productKey]) ? prev[productKey] : []).map((sel, idx) => 
                                            idx === selectionIndex 
                                              ? { ...sel, quantity: quantity + 1 }
                                              : sel
                                          )
                                        }));
                                      }}
                                    >
                                      +
                                    </button>
                                    <button
                                      type="button"
                                      className="product-search-qty-btn product-search-qty-btn-remove"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedOptions(prev => ({
                                          ...prev,
                                          [productKey]: (Array.isArray(prev[productKey]) ? prev[productKey] : []).filter((_, idx) => idx !== selectionIndex)
                                        }));
                                      }}
                                      title="Supprimer cette sélection"
                                    >
                                      ×
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      
                      {/* Alerte pour options manquantes */}
                      {alertMessage && alertMessage.productId === p.id && (
                        <div className="product-search-alert">
                          <div className="product-search-alert-icon">⚠️</div>
                          <div className="product-search-alert-content">
                            <div className="product-search-alert-title">Options manquantes</div>
                            <div className="product-search-alert-message">{alertMessage.message}</div>
                          </div>
                          <button
                            className="product-search-alert-close"
                            onClick={() => setAlertMessage(null)}
                          >
                            ×
                          </button>
                        </div>
                      )}
                      
                      {/* Bouton ajouter au panier - fonctionne avec les sélections */}
                      <button
                        className="product-search-add-to-cart-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          const productKey = p.id;
                          const productSelections = Array.isArray(selectedOptions[productKey]) ? selectedOptions[productKey] : [];
                          
                          // Vérifier les options disponibles
                            const details = productsDetails.get(p.id);
                            const variants = details?.variants || [];
                          
                          // Extraire les options disponibles
                          const availableSizes = new Set();
                          const availableColors = new Set();
                          const availablePackagings = new Set();
                          
                          variants.forEach(v => {
                            const sizeValue = v.attributes?.size || v.size;
                            if (sizeValue) availableSizes.add(sizeValue);
                            
                            const colorValue = v.attributes?.color || v.color;
                            if (colorValue) availableColors.add(colorValue);
                            
                            const packagingValue = v.packaging || v.quantity || v.attributes?.quantity_per_pack;
                            if (packagingValue) availablePackagings.add(packagingValue);
                          });
                          
                          const sizesArray = Array.from(availableSizes);
                          const colorsArray = Array.from(availableColors);
                          const packagingsArray = Array.from(availablePackagings);
                          
                          const hasMultipleSizes = sizesArray.length > 1;
                          const hasMultipleColors = colorsArray.length > 1;
                          const hasMultiplePackagings = packagingsArray.length > 1;
                          
                          // Si aucune sélection, valider et créer une sélection par défaut ou afficher une alerte
                          if (productSelections.length === 0) {
                            // Vérifier si des options multiples nécessitent une sélection
                            const missingOptions = [];
                            if (hasMultipleSizes) missingOptions.push('une taille');
                            if (hasMultipleColors) missingOptions.push('une couleur');
                            if (hasMultiplePackagings) missingOptions.push('un conditionnement');
                            
                            if (missingOptions.length > 0) {
                              // Afficher une alerte pour demander de sélectionner les options manquantes
                              setAlertMessage({
                                productId: p.id,
                                message: `Veuillez sélectionner ${missingOptions.join(', ')}.`
                              });
                              // Fermer l'alerte après 5 secondes
                              setTimeout(() => setAlertMessage(null), 5000);
                              return;
                            }
                            
                            // Toutes les options sont uniques ou absentes, créer une sélection automatique
                            const firstVariant = variants[0];
                            
                            if (firstVariant) {
                              // Récupérer le packaging depuis la variante
                              const defaultPackaging = firstVariant.packaging || firstVariant.quantity || firstVariant.attributes?.quantity_per_pack || null;
                              const defaultSelection = {
                                size: sizesArray[0] || firstVariant.attributes?.size || firstVariant.size || null,
                                color: colorsArray[0] || firstVariant.attributes?.color || firstVariant.color || null,
                                packaging: packagingsArray[0] || defaultPackaging,
                                quantity: 1
                              };
                              
                              setSelectedOptions(prev => ({
                                ...prev,
                                [productKey]: [defaultSelection]
                              }));
                              
                              // Ajouter au panier après création
                              setTimeout(() => {
                                handleAddToCartWithSelection(p, defaultSelection);
                              }, 100);
                            } else {
                              handleAddToCart(p, e);
                            }
                          } else {
                            // Valider que chaque sélection a toutes les options nécessaires
                            const invalidSelections = productSelections.filter(selection => {
                              const needsSize = hasMultipleSizes && !selection.size;
                              const needsColor = hasMultipleColors && !selection.color;
                              const needsPackaging = hasMultiplePackagings && !selection.packaging;
                              return needsSize || needsColor || needsPackaging;
                            });
                            
                            if (invalidSelections.length > 0) {
                              const missingOptions = [];
                              if (hasMultipleSizes) missingOptions.push('une taille');
                              if (hasMultipleColors) missingOptions.push('une couleur');
                              if (hasMultiplePackagings) missingOptions.push('un conditionnement');
                              
                              setAlertMessage({
                                productId: p.id,
                                message: `Veuillez sélectionner ${missingOptions.join(', ')} pour toutes les sélections.`
                              });
                              setTimeout(() => setAlertMessage(null), 5000);
                              return;
                            }
                            // Remplacer les anciennes sélections du panier pour ce produit par les nouvelles
                            // Créer tous les nouveaux items de panier d'abord
                            const details = productsDetails.get(p.id);
                            const variants = details?.variants || [];
                            
                            const newCartItems = productSelections.map(selection => {
                              // Trouver la variante correspondante en incluant le packaging
                              const matchingVariant = variants.find(v => {
                                const sizeMatch = !selection.size || (v.attributes?.size || v.size) === selection.size;
                                const colorMatch = !selection.color || (v.attributes?.color || v.color) === selection.color;
                                const packagingMatch = !selection.packaging || 
                                  v.packaging === selection.packaging || 
                                  v.attributes?.quantity_per_pack === selection.packaging ||
                                  v.quantity === selection.packaging;
                                return sizeMatch && colorMatch && packagingMatch;
                              }) || variants[0];
                              
                              // Extraire la référence GACD
                              const gacdRef = details?.product?.gacdReference || details?.references?.find(ref => ref.source === "GACD")?.ref || null;
                              
                              return {
                                id: p.id,
                                variantId: matchingVariant?.id || null,
                                name: p.name || p.title || '',
                                brand: p.brand,
                                category: p.category || '',
                                subcategory: p.subCategory || p.subcategory || '',
                                price: matchingVariant?.attributes?.price || details?.aggregates?.avg_price || (p.variants?.[0]?.reducedPrice ?? p.variants?.[0]?.referencePrice) || 0,
                                quantity: selection.quantity || 1,
                                thumbnail_url: p.thumbnail_url,
                                packaging: selection.packaging || matchingVariant?.packaging || matchingVariant?.quantity || null,
                                variant_attributes: {
                                  ...(selection.size ? { size: selection.size } : {}),
                                  ...(selection.color ? { color: selection.color } : {}),
                                  ...(selection.packaging ? { packaging: selection.packaging } : {}),
                                  ...(matchingVariant?.attributes || {})
                                },
                                variant_url: matchingVariant?.variant_url || details?.product?.canonical_url || p.canonical_url || null,
                                gacdReference: gacdRef
                              };
                            });
                            
                            // Remplacer atomiquement : supprimer les anciennes entrées de ce produit et ajouter les nouvelles
                            setCart(prev => {
                              // Garder tous les items qui ne sont pas de ce produit
                              const otherItems = prev.filter(item => item.id !== p.id);
                              // Ajouter les nouvelles sélections
                              return [...otherItems, ...newCartItems];
                            });
                          }
                        }}
                      >
                        Ajouter au panier
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

          {/* Cart Sidebar - Right Column (toujours visible si panier non vide) */}
        {cart.length > 0 && (
            <div className="product-search-cart-sidebar">
              <div className="product-search-cart-sidebar-header">
              <span className="product-search-cart-icon">🛒</span>
              <span>Panier ({cart.length})</span>
              <span className="product-search-cart-total">{cartTotal.toFixed(2)}€</span>
            </div>
              <div className="product-search-cart-items">
                {cart.map((item, index) => (
                  <div key={index} className="product-search-cart-item">
                    <div className="product-search-cart-item-info">
                      <div className="product-search-cart-item-title">{item.name}</div>
                      {item.variant_attributes && (
                        <div className="product-search-cart-item-variants">
                          {item.variant_attributes.size && (
                            <span className="product-search-cart-item-variant">Taille: {item.variant_attributes.size}</span>
                          )}
                          {item.variant_attributes.color && (
                            <span className="product-search-cart-item-variant">Couleur: {item.variant_attributes.color}</span>
                          )}
                        </div>
                      )}
                      <div className="product-search-cart-item-price">{item.price.toFixed(2)}€ × {item.quantity}</div>
                    </div>
                    <div className="product-search-cart-item-actions">
            <button 
                        className="product-search-cart-item-remove"
                        onClick={() => {
                          setCart(prev => prev.filter((_, i) => i !== index));
                        }}
                        title="Supprimer"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="product-search-cart-sidebar-footer">
                <div className="product-search-cart-sidebar-total">
                  <span>Total:</span>
                  <span className="product-search-cart-total-amount">{cartTotal.toFixed(2)}€</span>
                </div>
                <button 
                  className="product-search-cart-sidebar-btn"
                  onClick={() => {
                    // Générer le texte du mail (cabinet déjà chargé)
                    const emailBody = generateEmailBody(cart);
                    setEmailForm(prev => ({
                      ...prev,
                      body: emailBody
                    }));
                    
                    // Afficher le formulaire email
                    setShowEmailForm(true);
                  }}
                >
                  Valider la commande
                </button>
              </div>
          </div>
        )}
        </div>

        {/* Empty state */}
        {open && filteredProducts.length === 0 && query && (
          <div className="product-search-empty">
            <div className="product-search-empty-icon">🔍</div>
            <p>Aucun produit trouvé pour "{query}"</p>
          </div>
        )}

      </div>
    </div>
  );
}

export default ProductSearchModal;

